import { Link } from 'react-router-dom';
import { Bar, BarChart, Rectangle, ReferenceLine, XAxis, YAxis } from 'recharts';
import type { TooltipPayloadEntry, TooltipValueType } from 'recharts';
import { Card, CardContent } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, type ChartConfig } from '@/components/ui/chart';
import { t } from '@/i18n';
import type { ProjectListItem } from '@ip/shared-types';
import { ChartCard } from './ChartCard';
import { DashboardSection } from './DashboardSection';
import { formatAxis, formatPLN, formatPct } from './chart-utils';

/**
 * Per-project P&L as a DIVERGING chart: profit and loss are two sides of a
 * baseline, not two categories. Per the color formula that means two hues that
 * read as opposite plus a neutral zero — blue (profit) ↔ red (loss) with a gray
 * zero rule. Blue↔red, not green↔red: the validator puts blue↔red at ΔE 86.2
 * under protanopia vs 20.1 for green↔red.
 *
 * The list is bounded: top N by |profit| plus an explicit "others" roll-up, so
 * a company with 60 projects gets a readable chart instead of an unbounded bar
 * list. When anything is rolled up the UI says so — the roll-up is a labelled
 * bar AND a note, never a silent truncation.
 */
const TOP_N = 8;

interface Row {
  id: string | null;
  name: string;
  costNet: number;
  revenueNet: number;
  profit: number;
  marginPct: number | null;
  rolledUpCount: number;
}

/**
 * Bar shape for the diverging arms: the 4px radius rides the DATA end (right
 * for profit, left for loss) and stays square where the bar meets the zero
 * baseline. Color follows the sign, not the row's rank.
 */
function DivergingBar(props: unknown) {
  const bar = props as { profit?: number } & Record<string, unknown>;
  const positive = (bar.profit ?? 0) >= 0;
  return (
    <Rectangle
      {...bar}
      fill={positive ? 'var(--chart-positive)' : 'var(--chart-negative)'}
      radius={positive ? [0, 4, 4, 0] : [4, 0, 0, 4]}
    />
  );
}

function ProjectTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: TooltipPayloadEntry<TooltipValueType, number | string>[];
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload as Row | undefined;
  if (!row) return null;

  const positive = row.profit >= 0;

  return (
    <div className="border-border/50 bg-background grid min-w-[14rem] items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl">
      <div className="font-medium">{row.name}</div>
      <div className="grid gap-1.5">
        <div className="flex w-full items-center gap-2">
          <div
            className="size-2.5 shrink-0 rounded-[2px]"
            style={{ background: positive ? 'var(--chart-positive)' : 'var(--chart-negative)' }}
          />
          <span className="text-muted-foreground">
            {positive ? t('dashboard.projects.profit') : t('dashboard.projects.loss')}
          </span>
          <span className="text-foreground ml-auto pl-4 font-mono font-medium tabular-nums">
            {formatPLN(row.profit)} PLN
          </span>
        </div>
        <div className="flex w-full items-center gap-2">
          <div className="size-2.5 shrink-0" aria-hidden="true" />
          <span className="text-muted-foreground">{t('dashboard.projects.revenue')}</span>
          <span className="text-foreground ml-auto pl-4 font-mono font-medium tabular-nums">
            {formatPLN(row.revenueNet)} PLN
          </span>
        </div>
        <div className="flex w-full items-center gap-2">
          <div className="size-2.5 shrink-0" aria-hidden="true" />
          <span className="text-muted-foreground">{t('dashboard.projects.cost')}</span>
          <span className="text-foreground ml-auto pl-4 font-mono font-medium tabular-nums">
            {formatPLN(row.costNet)} PLN
          </span>
        </div>
        {row.marginPct != null && (
          <div className="flex w-full items-center gap-2">
            <div className="size-2.5 shrink-0" aria-hidden="true" />
            <span className="text-muted-foreground">{t('dashboard.projects.margin')}</span>
            <span className="text-foreground ml-auto pl-4 font-mono font-medium tabular-nums">
              {formatPct(row.marginPct)}%
            </span>
          </div>
        )}
        {row.rolledUpCount > 0 && (
          <p className="text-muted-foreground border-border/50 mt-0.5 border-t pt-1.5 leading-snug">
            {t('dashboard.projects.rollupTooltip', { count: row.rolledUpCount })}
          </p>
        )}
      </div>
    </div>
  );
}

function EmptyProjects() {
  return (
    <Card className="border-dashed">
      <CardContent className="py-8">
        <p className="text-muted-foreground text-center text-sm">
          {t('dashboard.projects.empty')}
        </p>
      </CardContent>
    </Card>
  );
}

export function ProjectsSection({
  projects,
  companyNip,
}: {
  projects: ProjectListItem[];
  companyNip: string;
}) {
  const sectionAction = (
    <Link
      to={`/${companyNip}/projects`}
      className="text-muted-foreground hover:text-foreground shrink-0 text-xs font-medium underline underline-offset-4"
    >
      {t('dashboard.projects.allProjects')}
    </Link>
  );

  if (projects.length === 0) {
    return (
      <DashboardSection
        title={t('dashboard.projects.section')}
        subtitle={t('dashboard.projects.sectionSubtitle')}
        action={sectionAction}
      >
        <EmptyProjects />
      </DashboardSection>
    );
  }

  const ranked = [...projects].sort((a, b) => Math.abs(b.profit) - Math.abs(a.profit));
  const shown = ranked.slice(0, TOP_N);
  const rest = ranked.slice(TOP_N);

  const rows: Row[] = shown.map((p) => ({
    id: p.id,
    name: p.name,
    costNet: p.costNet,
    revenueNet: p.revenueNet,
    profit: p.profit,
    marginPct: p.marginPct,
    rolledUpCount: 0,
  }));

  if (rest.length > 0) {
    const revenueNet = rest.reduce((s, p) => s + p.revenueNet, 0);
    const costNet = rest.reduce((s, p) => s + p.costNet, 0);
    const profit = rest.reduce((s, p) => s + p.profit, 0);
    rows.push({
      id: null,
      name: t('dashboard.projects.othersLabel', { count: rest.length }),
      costNet,
      revenueNet,
      profit,
      marginPct: revenueNet > 0 ? (profit / revenueNet) * 100 : null,
      rolledUpCount: rest.length,
    });
  }

  // Recharts renders datum 0 at the TOP of a vertical-layout chart, and `rows`
  // is already sorted biggest-|profit|-first with the roll-up appended last —
  // so the chart order matches the table order with no reversing.
  const config: ChartConfig = {
    profit: { label: t('dashboard.projects.profit') },
  };

  return (
    <DashboardSection
      title={t('dashboard.projects.section')}
      subtitle={t('dashboard.projects.sectionSubtitle')}
      action={sectionAction}
    >
      <ChartCard title={t('dashboard.projects.plTitle')}>
        <p className="text-muted-foreground mb-3 text-xs">{t('dashboard.projects.plHint')}</p>
        <ChartContainer
          config={config}
          className="w-full"
          style={{ height: `${Math.max(rows.length * 44 + 40, 160)}px` }}
        >
          <BarChart data={rows} layout="vertical" margin={{ left: 4, right: 16 }} barSize={20}>
            <XAxis type="number" tickFormatter={formatAxis} tickLine={false} axisLine={false} />
            <YAxis
              type="category"
              dataKey="name"
              tickLine={false}
              axisLine={false}
              width={150}
              tick={{ fontSize: 12 }}
            />
            {/* Neutral zero rule — the diverging midpoint reads as "nothing". */}
            <ReferenceLine x={0} stroke="var(--chart-zero)" strokeWidth={1} />
            <ChartTooltip content={<ProjectTooltip />} />
            {/* Rounded 4px at the DATA end, square where the bar meets the zero
                baseline — the corner radius has to point away from the axis on
                both arms of a diverging chart. */}
            <Bar dataKey="profit" maxBarSize={20} shape={DivergingBar} />
          </BarChart>
        </ChartContainer>

        {rest.length > 0 && (
          <p className="text-muted-foreground mt-3 text-xs leading-snug">
            {t('dashboard.projects.rollupNote', { shown: TOP_N, count: rest.length })}
          </p>
        )}

        {/* Table view — the accessible twin: sign is not color-only, and every
            value the chart hides (revenue, cost, margin) is readable here. */}
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[32rem] text-xs">
            <thead>
              <tr className="text-muted-foreground border-b text-left">
                <th scope="col" className="py-1.5 pr-3 font-medium">
                  {t('dashboard.projects.colProject')}
                </th>
                <th scope="col" className="py-1.5 pr-3 text-right font-medium">
                  {t('dashboard.projects.revenue')}
                </th>
                <th scope="col" className="py-1.5 pr-3 text-right font-medium">
                  {t('dashboard.projects.cost')}
                </th>
                <th scope="col" className="py-1.5 pr-3 text-right font-medium">
                  {t('dashboard.projects.profit')}
                </th>
                <th scope="col" className="py-1.5 text-right font-medium">
                  {t('dashboard.projects.margin')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id ?? 'others'} className="border-b last:border-0">
                  <td className="max-w-[14rem] truncate py-1.5 pr-3">
                    {row.id ? (
                      <Link
                        to={`/${companyNip}/projects/${row.id}`}
                        className="font-medium underline-offset-4 hover:underline"
                        title={row.name}
                      >
                        {row.name}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">{row.name}</span>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">
                    {formatPLN(row.revenueNet)}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{formatPLN(row.costNet)}</td>
                  <td
                    className={`py-1.5 pr-3 text-right font-medium tabular-nums ${
                      row.profit < 0 ? 'text-rose-600 dark:text-rose-400' : ''
                    }`}
                  >
                    {formatPLN(row.profit)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    {row.marginPct != null ? `${formatPct(row.marginPct)}%` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>
    </DashboardSection>
  );
}
